import { KbSlider } from '@theme/kb-slider';

if (!customElements.get('social-content-carousel')) {
  class SocialContentCarousel extends HTMLElement {
    constructor() {
      super();

      this.slider = null;
      this.activeIndex = 0;
      this.slideCount = 0;
      this.isSetup = false;
      this.isDesignMode = false;
      this.loopEnabled = false;
      this.autoplayEnabled = false;
      this.autoplayInterval = 5000;
      this.autoplayTimer = null;
      this.isPointerOver = false;
      this.abortController = null;

      this.handleClick = this.handleClick.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleVisibilityChange =
        this.handleVisibilityChange.bind(this);
      this.handlePointerEnter =
        this.handlePointerEnter.bind(this);
      this.handlePointerLeave =
        this.handlePointerLeave.bind(this);
      this.handleInteractionStart =
        this.handleInteractionStart.bind(this);
      this.handleInteractionEnd =
        this.handleInteractionEnd.bind(this);
      this.handleVideoPlay =
        this.handleVideoPlay.bind(this);
      this.handleVideoPause =
        this.handleVideoPause.bind(this);
      this.handleVideoEnded =
        this.handleVideoEnded.bind(this);
    }

    connectedCallback() {
      if (this.isSetup) {
        return;
      }

      this.track =
        this.querySelector(
          '[data-social-carousel-track]'
        ) ||
        this.querySelector(
          '.social-content-carousel__track'
        );

      this.progress = this.querySelector(
        '[data-social-carousel-progress]'
      );

      this.status = this.querySelector(
        '[data-social-carousel-status]'
      );

      this.slides = Array.from(
        this.querySelectorAll(
          '[data-social-carousel-original]'
        )
      );

      this.slideCount = this.slides.length;

      if (!this.track || this.slideCount === 0) {
        return;
      }

      this.isSetup = true;
      this.isDesignMode = Boolean(
        window.Shopify?.designMode
      );

      this.loopEnabled =
        this.dataset.loop === 'true' &&
        this.slideCount > 1;

      this.autoplayEnabled =
        this.dataset.autoplay === 'true';

      this.autoplayInterval =
        Number.parseInt(
          this.dataset.autoplayInterval,
          10
        ) || 5000;

      this.abortController =
        new AbortController();

      this.style.setProperty(
        '--social-content-carousel-slide-count',
        this.slideCount
      );

      this.disableNativeMediaDragging();
      this.addEventListeners();

      if (this.slideCount === 1) {
        this.classList.add('is-initialised');
        this.updateActiveState(0);

        return;
      }

      this.initialiseSlider();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      this.pauseAllVideos();

      this.abortController?.abort();
      this.abortController = null;

      this.slider?.destroy();
      this.slider = null;

      this.classList.remove('is-initialised');

      this.isSetup = false;
      this.activeIndex = 0;
      this.isPointerOver = false;
    }

    initialiseSlider() {
      try {
        this.slider = new KbSlider(
          this.track,
          {
            loop: this.loopEnabled,
            drag: true,
            keyboard: true,
            snapAlign: 'center',
            ariaLabel:
              'Social content carousel',
            progressHost: this,
            transitionMs:
              this.getTransitionDuration(),

            onChange: ({
              index,
              trigger
            }) => {
              this.handleSliderChange(
                index,
                trigger
              );
            },

            onSettle: () => {
              this.handleSliderSettle();
            }
          }
        );

        this.slider.setSlides(this.slides);

        this.classList.add('is-initialised');

        this.updateActiveState(
          this.slider.index
        );

        this.startAutoplay();
      } catch (error) {
        console.error(
          'Social Content Carousel: Failed to initialise.',
          error
        );

        this.slider = null;

        this.classList.add('is-initialised');
        this.updateActiveState(0);
        this.enableFallbackAccessibility();
      }
    }

    addEventListeners() {
      const signal =
        this.abortController.signal;

      this.addEventListener(
        'click',
        this.handleClick,
        { signal }
      );

      this.addEventListener(
        'play',
        this.handleVideoPlay,
        {
          capture: true,
          signal
        }
      );

      this.addEventListener(
        'pause',
        this.handleVideoPause,
        {
          capture: true,
          signal
        }
      );

      this.addEventListener(
        'ended',
        this.handleVideoEnded,
        {
          capture: true,
          signal
        }
      );

      this.addEventListener(
        'pointerenter',
        this.handlePointerEnter,
        { signal }
      );

      this.addEventListener(
        'pointerleave',
        this.handlePointerLeave,
        { signal }
      );

      this.track.addEventListener(
        'pointerdown',
        this.handleInteractionStart,
        { signal }
      );

      this.track.addEventListener(
        'pointerup',
        this.handleInteractionEnd,
        { signal }
      );

      this.track.addEventListener(
        'pointercancel',
        this.handleInteractionEnd,
        { signal }
      );

      this.track.addEventListener(
        'touchstart',
        this.handleInteractionStart,
        {
          passive: true,
          signal
        }
      );

      this.track.addEventListener(
        'touchend',
        this.handleInteractionEnd,
        {
          passive: true,
          signal
        }
      );

      this.track.addEventListener(
        'touchcancel',
        this.handleInteractionEnd,
        {
          passive: true,
          signal
        }
      );

      document.addEventListener(
        'visibilitychange',
        this.handleVisibilityChange,
        { signal }
      );

      if (this.isDesignMode) {
        document.addEventListener(
          'shopify:block:select',
          this.handleBlockSelect,
          { signal }
        );
      }
    }

    handleSliderChange(index, trigger) {
      this.pauseAllVideos();
      this.updateActiveState(index);

      if (trigger !== 'autoplay') {
        this.restartAutoplay();
      } else {
        this.startAutoplay();
      }
    }

    handleSliderSettle() {
      this.updateActiveState(
        this.slider?.index ?? 0
      );
    }

    handleClick(event) {
      if (!(event.target instanceof Element)) {
        return;
      }

      const media = event.target.closest(
        '.social-content-carousel__media'
      );

      if (!media || !this.contains(media)) {
        return;
      }

      const video = media.querySelector(
        '[data-social-carousel-video]'
      );

      if (!video) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (video.paused || video.ended) {
        this.playVideo(video);

        return;
      }

      video.pause();
    }

    handleBlockSelect(event) {
      if (!(event.target instanceof Element)) {
        return;
      }

      const selectedSlide =
        event.target.closest(
          '[data-social-carousel-original]'
        );

      if (
        !selectedSlide ||
        !this.contains(selectedSlide) ||
        !this.slider
      ) {
        return;
      }

      const selectedIndex =
        this.slides.indexOf(selectedSlide);

      if (selectedIndex < 0) {
        return;
      }

      this.stopAutoplay();
      this.pauseAllVideos();

      this.slider.goTo(
        selectedIndex,
        {
          animate: false,
          trigger: 'editor'
        }
      );

      this.restartAutoplay();
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.stopAutoplay();
        this.pauseAllVideos();

        return;
      }

      this.startAutoplay();
    }

    handlePointerEnter() {
      this.isPointerOver = true;
      this.stopAutoplay();
    }

    handlePointerLeave() {
      this.isPointerOver = false;
      this.startAutoplay();
    }

    handleInteractionStart() {
      this.stopAutoplay();
    }

    handleInteractionEnd() {
      window.requestAnimationFrame(() => {
        this.startAutoplay();
      });
    }

    handleVideoPlay(event) {
      const video = event.target;

      if (!(video instanceof HTMLVideoElement)) {
        return;
      }

      const slide = video.closest(
        '[data-social-carousel-slide]'
      );

      if (!slide) {
        video.pause();

        return;
      }

      this.stopAutoplay();
      this.pauseAllVideos(video);

      slide.classList.add(
        'is-started',
        'is-playing'
      );

      this.updateSlideMediaAccessibility(
        slide
      );
    }

    handleVideoPause(event) {
      const video = event.target;

      if (!(video instanceof HTMLVideoElement)) {
        return;
      }

      const slide = video.closest(
        '[data-social-carousel-slide]'
      );

      if (!slide) {
        return;
      }

      slide.classList.remove(
        'is-playing'
      );

      this.updateSlideMediaAccessibility(
        slide
      );

      this.startAutoplay();
    }

    handleVideoEnded(event) {
      const video = event.target;

      if (!(video instanceof HTMLVideoElement)) {
        return;
      }

      const slide = video.closest(
        '[data-social-carousel-slide]'
      );

      if (!slide) {
        return;
      }

      slide.classList.remove(
        'is-playing'
      );

      this.updateSlideMediaAccessibility(
        slide
      );

      this.startAutoplay();
    }

    updateActiveState(index) {
      const boundedIndex = Math.min(
        Math.max(
          Number.isInteger(index)
            ? index
            : 0,
          0
        ),
        this.slideCount - 1
      );

      this.activeIndex = boundedIndex;

      this.style.setProperty(
        '--social-content-carousel-active-index',
        boundedIndex
      );

      this.progress?.setAttribute(
        'aria-valuenow',
        boundedIndex + 1
      );

      this.slides.forEach(
        (slide, slideIndex) => {
          const isActive =
            slideIndex === boundedIndex;

          slide.classList.toggle(
            'social-content-carousel__slide--active',
            isActive
          );

          slide.classList.toggle(
            'is-active',
            isActive
          );

          slide.setAttribute(
            'aria-current',
            isActive
              ? 'true'
              : 'false'
          );

          const video = slide.querySelector(
            '[data-social-carousel-video]'
          );

          if (
            isActive &&
            video?.preload === 'none'
          ) {
            video.preload = 'metadata';
          }

          this.updateSlideMediaAccessibility(
            slide
          );
        }
      );

      this.updateRenderedSlides();
      this.updateStatus();
    }

    updateRenderedSlides() {
      const renderedSlides = Array.from(
        this.track.querySelectorAll(
          '[data-social-carousel-slide]'
        )
      );

      renderedSlides.forEach((slide) => {
        const realIndex =
          this.getRenderedSlideIndex(slide);

        const isActive =
          realIndex === this.activeIndex;

        slide.classList.toggle(
          'social-content-carousel__slide--active',
          isActive
        );

        slide.classList.toggle(
          'is-active',
          isActive
        );

        /*
         * Clones remain hidden from assistive
         * technology and keyboard navigation.
         * KbSlider owns their accessibility.
         */
        if (
          slide.dataset.kbSliderClone
        ) {
          return;
        }

        slide.setAttribute(
          'aria-current',
          isActive
            ? 'true'
            : 'false'
        );
      });
    }

    updateSlideMediaAccessibility(slide) {
      const video = slide.querySelector(
        '[data-social-carousel-video]'
      );

      const playButton =
        slide.querySelector(
          '[data-social-carousel-play]'
        );

      const hasStarted =
        slide.classList.contains(
          'is-started'
        );

      if (video) {
        video.controls = false;
        video.tabIndex = -1;
      }

      if (playButton) {
        playButton.tabIndex =
          hasStarted ? -1 : 0;

        playButton.setAttribute(
          'aria-hidden',
          hasStarted
            ? 'true'
            : 'false'
        );
      }
    }

    updateStatus() {
      if (!this.status) {
        return;
      }

      this.status.textContent =
        `Slide ${this.activeIndex + 1} ` +
        `of ${this.slideCount}`;
    }

    async playVideo(video) {
      this.stopAutoplay();
      this.pauseAllVideos(video);

      try {
        await video.play();
      } catch (error) {
        const slide = video.closest(
          '[data-social-carousel-slide]'
        );

        slide?.classList.remove(
          'is-started',
          'is-playing'
        );

        if (slide) {
          this.updateSlideMediaAccessibility(
            slide
          );
        }

        this.startAutoplay();
      }
    }

    pauseAllVideos(excludedVideo = null) {
      const videos = Array.from(
        this.querySelectorAll(
          '[data-social-carousel-video]'
        )
      );

      videos.forEach((video) => {
        if (video === excludedVideo) {
          return;
        }

        if (!video.paused) {
          video.pause();
        }

        video
          .closest(
            '[data-social-carousel-slide]'
          )
          ?.classList.remove(
            'is-playing'
          );
      });
    }

    hasPlayingVideo() {
      return Array.from(
        this.querySelectorAll(
          '[data-social-carousel-video]'
        )
      ).some(
        (video) =>
          !video.paused &&
          !video.ended
      );
    }

    startAutoplay() {
      this.stopAutoplay();

      if (
        !this.autoplayEnabled ||
        !this.slider ||
        this.slideCount <= 1 ||
        document.hidden ||
        this.isPointerOver ||
        this.hasPlayingVideo()
      ) {
        return;
      }

      this.autoplayTimer =
        window.setTimeout(() => {
          this.autoplayTimer = null;

          if (
            !this.slider ||
            this.hasPlayingVideo()
          ) {
            return;
          }

          this.slider.next('autoplay');
        }, this.autoplayInterval);
    }

    stopAutoplay() {
      if (!this.autoplayTimer) {
        return;
      }

      window.clearTimeout(
        this.autoplayTimer
      );

      this.autoplayTimer = null;
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }

    getRenderedSlideIndex(slide) {
      if (!slide) {
        return -1;
      }

      const cloneIndex =
        Number.parseInt(
          slide.dataset.kbSliderRealIndex,
          10
        );

      if (Number.isInteger(cloneIndex)) {
        return cloneIndex;
      }

      return this.slides.indexOf(slide);
    }

    disableNativeMediaDragging() {
      this.querySelectorAll(
        'img, video'
      ).forEach((media) => {
        media.draggable = false;
      });

      this.querySelectorAll(
        '[data-social-carousel-video]'
      ).forEach((video) => {
        video.controls = false;
        video.playsInline = true;
      });
    }

    enableFallbackAccessibility() {
      this.slides.forEach((slide) => {
        slide.classList.add(
          'is-full-opacity'
        );

        const video = slide.querySelector(
          '[data-social-carousel-video]'
        );

        const playButton =
          slide.querySelector(
            '[data-social-carousel-play]'
        );

        if (video) {
          video.controls = true;
          video.tabIndex = 0;
        }

        if (playButton) {
          playButton.tabIndex = -1;

          playButton.setAttribute(
            'aria-hidden',
            'true'
          );
        }
      });
    }

    getTransitionDuration() {
      const propertyValue = window
        .getComputedStyle(this)
        .getPropertyValue(
          '--social-content-carousel-transition-duration'
        )
        .trim();

      if (
        propertyValue.endsWith('ms')
      ) {
        return Number.parseFloat(
          propertyValue
        );
      }

      if (
        propertyValue.endsWith('s')
      ) {
        return (
          Number.parseFloat(
            propertyValue
          ) * 1000
        );
      }

      return 700;
    }
  }

  customElements.define(
    'social-content-carousel',
    SocialContentCarousel
  );
}